import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useAuthUser } from "../contexts/AuthUserContext";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  SEND_SUPPORT_TICKET,
  REPORT_BUG,
  REQUEST_FEATURE,
  REQUEST_INTEGRATION,
} from "../graphql/mutations";
import styles from "./SupportCenterModal.module.css";

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

const modeConfig = {
  support: {
    titleKey: "ContactSupportModal.title",
    emailLabelKey: "ContactSupportModal.emailLabel",
    emailPlaceholderKey: "ContactSupportModal.emailPlaceholder",
    invalidEmailKey: "ContactSupportModal.invalidEmail",
    messageLabelKey: "ContactSupportModal.messageLabel",
    messagePlaceholderKey: "ContactSupportModal.messagePlaceholder",
    shortMsgKey: "ContactSupportModal.shortMsg",
    sendKey: "ContactSupportModal.send",
    sendingKey: "ContactSupportModal.sending",
    toastKey: "ContactSupportModal.toast",
  },
  bug: {
    titleKey: "ReportBugModal.title",
    emailLabelKey: "ReportBugModal.emailLabel",
    emailPlaceholderKey: "ReportBugModal.emailPlaceholder",
    invalidEmailKey: "ReportBugModal.invalidEmail",
    messageLabelKey: "ReportBugModal.messageLabel",
    messagePlaceholderKey: "ReportBugModal.messagePlaceholder",
    shortMsgKey: "ReportBugModal.shortMsg",
    sendKey: "ReportBugModal.send",
    sendingKey: "ReportBugModal.sending",
    toastKey: "ReportBugModal.toast",
  },
  feature: {
    titleKey: "RequestFeatureModal.title",
    emailLabelKey: "RequestFeatureModal.emailLabel",
    emailPlaceholderKey: "RequestFeatureModal.emailPlaceholder",
    invalidEmailKey: "RequestFeatureModal.invalidEmail",
    messageLabelKey: "RequestFeatureModal.messageLabel",
    messagePlaceholderKey: "RequestFeatureModal.messagePlaceholder",
    shortMsgKey: "RequestFeatureModal.shortMsg",
    sendKey: "RequestFeatureModal.send",
    sendingKey: "RequestFeatureModal.sending",
    toastKey: "RequestFeatureModal.toast",
  },
  integration: {
    titleKey: "RequestIntegrationModal.title",
    emailLabelKey: "RequestIntegrationModal.emailLabel",
    emailPlaceholderKey: "RequestIntegrationModal.emailPlaceholder",
    invalidEmailKey: "RequestIntegrationModal.invalidEmail",
    messageLabelKey: "RequestIntegrationModal.messageLabel",
    messagePlaceholderKey: "RequestIntegrationModal.messagePlaceholder",
    shortMsgKey: "RequestIntegrationModal.shortMsg",
    sendKey: "RequestIntegrationModal.send",
    sendingKey: "RequestIntegrationModal.sending",
    toastKey: "RequestIntegrationModal.toast",
  },
};

const modeTabs = [
  { key: "support", labelKey: "DrawerSection.contactSupport" },
  { key: "bug", labelKey: "DrawerSection.reportBug" },
  { key: "feature", labelKey: "DrawerSection.requestFeature" },
  { key: "integration", labelKey: "DrawerSection.requestIntegration" },
];

const SupportCenterModal = ({ isOpen, mode = "support", onClose }) => {
  const { user } = useAuthUser();
  const { t, locale } = useLocalization();
  const authToken = user?.token;
  const fallbackEmail = user?.email || "";
  const isRtl = locale === "ar";

  const [activeMode, setActiveMode] = useState(mode);
  const [email, setEmail] = useState(fallbackEmail);
  const [message, setMessage] = useState("");
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [touchedMsg, setTouchedMsg] = useState(false);
  const [toast, setToast] = useState({ visible: false, text: "" });

  const [sendSupport, { loading: supportLoading }] = useMutation(
    SEND_SUPPORT_TICKET
  );
  const [reportBug, { loading: bugLoading }] = useMutation(REPORT_BUG);
  const [requestFeature, { loading: featureLoading }] = useMutation(
    REQUEST_FEATURE
  );
  const [requestIntegration, { loading: integrationLoading }] = useMutation(
    REQUEST_INTEGRATION
  );

  useEffect(() => {
    if (!isOpen) return;
    setActiveMode(mode);
    setEmail(fallbackEmail);
    setMessage("");
    setTouchedEmail(false);
    setTouchedMsg(false);
    setToast({ visible: false, text: "" });
  }, [isOpen, mode, fallbackEmail]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const config = modeConfig[activeMode] || modeConfig.support;
  const emailValid = isEmail(email);
  const msgValid = message.trim().length >= 5;
  const loadingByMode = {
    support: supportLoading,
    bug: bugLoading,
    feature: featureLoading,
    integration: integrationLoading,
  };
  const loading = loadingByMode[activeMode];
  const canSend = emailValid && msgValid && !loading;

  const mutationByMode = useMemo(
    () => ({
      support: sendSupport,
      bug: reportBug,
      feature: requestFeature,
      integration: requestIntegration,
    }),
    [sendSupport, reportBug, requestFeature, requestIntegration]
  );

  const showToast = useCallback(
    (text) => {
      setToast({ visible: true, text });
      window.setTimeout(() => {
        setToast((prev) => ({ ...prev, visible: false }));
      }, 2500);
    },
    []
  );

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    try {
      const mutate = mutationByMode[activeMode];
      if (!mutate) return;
      await mutate({
        variables: { input: { email: email.trim(), message: message.trim() } },
        context: authToken
          ? { headers: { Authorization: `Bearer ${authToken}` } }
          : undefined,
      });
      setEmail(fallbackEmail);
      setMessage("");
      setTouchedEmail(false);
      setTouchedMsg(false);
      showToast(t(config.toastKey));
      window.setTimeout(() => onClose?.(), 900);
    } catch (error) {
      console.error("Support center error:", error?.message || error);
      showToast(t(config.toastKey));
    }
  }, [
    canSend,
    mutationByMode,
    activeMode,
    email,
    message,
    authToken,
    fallbackEmail,
    showToast,
    t,
    config.toastKey,
    onClose,
  ]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={`${styles.modal} ${isRtl ? styles.rtl : ""}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {toast.visible && (
          <div className={styles.toast} role="status">
            {toast.text}
          </div>
        )}

        <div className={styles.header}>
          <div>
            <p className={styles.eyebrow}>{t("HelpCenterScreen.title")}</p>
            <h3 className={styles.title}>{t(config.titleKey)}</h3>
          </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          x
        </button>
        </div>

        <div className={styles.tabs} role="tablist">
          {modeTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${
                activeMode === tab.key ? styles.tabActive : ""
              }`}
              onClick={() => setActiveMode(tab.key)}
              role="tab"
              aria-selected={activeMode === tab.key}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        <div className={styles.body}>
          <label className={styles.label}>
            {t(config.emailLabelKey)}
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouchedEmail(true)}
              placeholder={t(config.emailPlaceholderKey)}
              className={`${styles.input} ${
                touchedEmail && !emailValid ? styles.inputError : ""
              }`}
            />
            {touchedEmail && !emailValid && (
              <span className={styles.errorText}>{t(config.invalidEmailKey)}</span>
            )}
          </label>

          <label className={styles.label}>
            {t(config.messageLabelKey)}
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              onBlur={() => setTouchedMsg(true)}
              placeholder={t(config.messagePlaceholderKey)}
              className={`${styles.textarea} ${
                touchedMsg && !msgValid ? styles.inputError : ""
              }`}
              rows={5}
              maxLength={6000}
            />
            {touchedMsg && !msgValid && (
              <span className={styles.errorText}>{t(config.shortMsgKey)}</span>
            )}
          </label>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={`${styles.sendButton} ${
              !canSend ? styles.sendButtonDisabled : ""
            }`}
            onClick={handleSend}
            disabled={!canSend}
          >
            {loading ? t(config.sendingKey) : t(config.sendKey)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportCenterModal;
